/**
 * @description Gateway-aware card entry wrapper.
 * Customer portal version — automatically renders HostedPaymentFields (Elavon HPF)
 * or PaymentCardForm (legacy) based on the active gateway's capabilities.
 *
 * Key features:
 * - Detects gateway type via getClientConfig() on mount
 * - Portal context: uses getPortalClientConfig(customerPublicId) (no auth required)
 * - Passes pre-fetched clientConfig to HPF hook to avoid redundant API call
 * - Falls back to legacy form if gateway detection fails
 * - Same props interface as PaymentCardForm (plus optional amount/currency/description
 *   which are passed through to HostedPaymentFields for HPF session creation)
 *
 * @dependencies
 * - paymentSessionService.getClientConfig(): Merchant-authenticated config endpoint
 * - paymentSessionService.getPortalClientConfig(): Portal-public config endpoint
 * - HostedPaymentFields: Elavon HPF card entry component
 * - PaymentCardForm: Legacy/mock card entry component
 *
 * @notes
 * - Gateway detection result is cached for the component's lifetime (single API call).
 * - HPF is only rendered AFTER config is fetched to prevent the hook from calling
 *   the auth-required endpoint independently.
 * - If gateway detection fails (network error, no gateway configured), falls back to
 *   legacy PaymentCardForm so the UI is never broken.
 */

import React, { useState, useEffect, useRef } from 'react';
import PaymentCardForm from './PaymentCardForm';
import HostedPaymentFields from './HostedPaymentFields';
import paymentSessionService from '../../services/paymentSessionService';

function CardEntry({ customerPublicId, ...props }) {
  const [gatewayConfig, setGatewayConfig] = useState(null);
  const [detecting, setDetecting] = useState(true);
  const fetchedRef = useRef(false);

  useEffect(() => {
    // Guard against double-fetch (React StrictMode)
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const detectGateway = async () => {
      try {
        // Portal context: use public endpoint (no auth required)
        // Dashboard context: use merchant-authenticated endpoint
        const config = customerPublicId
          ? await paymentSessionService.getPortalClientConfig(customerPublicId)
          : await paymentSessionService.getClientConfig();

        if (config?.features?.hostedFields) {
          setGatewayConfig(config);
        }
      } catch (err) {
        // Gateway not configured or network error — fall back to legacy form
        console.warn('Gateway detection failed, using legacy card form:', err.message);
      } finally {
        setDetecting(false);
      }
    };

    detectGateway();
  }, [customerPublicId]);

  // Loading state while detecting gateway
  if (detecting) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
        <svg className="animate-spin h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Preparing payment form...
      </div>
    );
  }

  // Elavon HPF — gateway supports hostedFields and config was fetched
  if (gatewayConfig) {
    return (
      <HostedPaymentFields
        {...props}
        clientConfig={gatewayConfig}
        customerPublicId={customerPublicId}
      />
    );
  }

  // Fallback: legacy form (no gateway configured, or detection failed)
  return <PaymentCardForm {...props} />;
}

export default CardEntry;
